import { IVariantValue } from '../types';

export const getGroupedVariants = (
  variants: IVariantValue[]
): IVariantValue[][] => {
  const groups = groupBy<IVariantValue, string | undefined>(
    variants,
    variant => variant.sizeSystem ?? undefined
  );
  const sortedGroups = groups.sort(
    ([aKey, a], [bKey, b]) =>
      getSizeSystemNumber(bKey) - getSizeSystemNumber(aKey)
  );

  return sortedGroups.map(([key, group]) => group.sort(compareVariants));
};

const compareVariants = (a: IVariantValue, b: IVariantValue) => {
  if (!a.size || !b.size) {
    return 0;
  }

  if (
    a.sizeSystem === 'CLOTHESNONNUMERIC' &&
    b.sizeSystem === 'CLOTHESNONNUMERIC'
  ) {
    return (
      getCloathesNonNumericNumber(a.size) - getCloathesNonNumericNumber(b.size)
    );
  }

  return a.size.localeCompare(b.size, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const getSizeSystemNumber = (sizeSystem?: string): number => {
  switch (sizeSystem) {
    case 'CLOTHESNUMERIC':
      return 1;
    case 'CLOTHESNONNUMERIC':
      return 2;
    default:
      return Infinity;
  }
};

const getCloathesNonNumericNumber = (size?: string): number => {
  if (!size) {
    return Infinity;
  }

  switch (size.toUpperCase()) {
    case 'XXS':
      return 1;
    case 'XS':
      return 2;
    case 'S':
      return 3;
    case 'S/M':
      return 4;
    case 'M':
      return 5;
    case 'M/L':
      return 6;
    case 'L':
      return 7;
    case 'L/XL':
      return 8;
    case 'XL':
      return 9;
    case 'XXL':
      return 10;
    case 'XXXL':
      return 11;
    case 'XXXXL':
      return 12;
    case 'XXXXXL':
      return 13;
    case 'ONE SIZE':
      return 14;

    default:
      return Infinity;
  }
};

function groupBy<E, K>(
  input: readonly E[],
  generateKey: (element: E) => K
): [K, E[]][] {
  const set = new Map<K, E[]>();

  for (const element of input) {
    const key = generateKey(element);

    if (!set.has(key)) {
      set.set(key, []);
    }

    set.get(key)?.push(element);
  }

  return Array.from(set.entries());
}
